import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const LEVEL_COLORS = {
  Junior: '#6b7280',
  Rising: '#3B82F6',
  Senior: '#FF6B00',
  Elite: '#FFD700',
};

const formatNum = (n) => {
  if (n === null || n === undefined || n === '') return '—';
  const num = parseFloat(n);
  if (isNaN(num)) return '—';
  return Math.round(num)?.toLocaleString('fr-FR')?.replace(/,/g, ' ');
};

const formatUSD = (n) => {
  if (n === null || n === undefined) return '—';
  const num = parseFloat(n);
  if (isNaN(num)) return '—';
  return '$' + num?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatHours = (decimal) => {
  if (!decimal && decimal !== 0) return '—';
  const val = parseFloat(decimal);
  if (isNaN(val)) return '—';
  const h = Math.floor(val);
  const m = Math.round((val - h) * 60);
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
};

const ServerBadge = ({ server, serversData }) => {
  const serverInfo = serversData?.find(s => s?.id === server);
  const color = serverInfo?.color || 'rgba(255,255,255,0.3)';
  return (
    <span
      className="px-2 py-0.5 rounded text-xs font-bold"
      style={{ backgroundColor: `${color}22`, color, fontFamily: 'Sora, sans-serif' }}
    >
      {server || '—'}
    </span>
  );
};

const LevelBadge = ({ level }) => {
  if (!level) return <span style={{ color: 'rgba(255,255,255,0.2)', fontFamily: 'Inter, sans-serif' }}>—</span>;
  const color = LEVEL_COLORS?.[level] || 'rgba(255,255,255,0.4)';
  return (
    <span
      className="px-2 py-0.5 rounded text-xs font-semibold"
      style={{ backgroundColor: `${color}22`, color, fontFamily: 'Inter, sans-serif', border: `1px solid ${color}44` }}
    >
      {level}
    </span>
  );
};

const TierBadge = ({ tierP }) => {
  if (tierP === null || tierP === undefined) return <span style={{ color: 'rgba(255,255,255,0.2)' }}>—</span>;
  const tier = parseInt(tierP);
  const colors = ['#FFD700', '#FFD700', '#f97316', '#f97316', '#3b82f6', '#3b82f6', '#6b7280', '#6b7280', '#6b7280', '#6b7280'];
  const color = colors?.[tier - 1] || '#6b7280';
  return (
    <span
      className="px-2 py-0.5 rounded text-xs font-bold"
      style={{ backgroundColor: `${color}22`, color, border: `1px solid ${color}44`, fontFamily: 'Sora, sans-serif' }}
    >
      T{tier}
    </span>
  );
};

const SortIcon = ({ column, sortCol, sortDir }) => {
  if (sortCol !== column) {
    return (
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ opacity: 0.3 }}>
        <path d="M5 2v6M2 5l3-3 3 3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  return sortDir === 'asc' ? (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ color: '#E5202A' }}>
      <path d="M5 8V2M2 5l3-3 3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ) : (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ color: '#E5202A' }}>
      <path d="M5 2v6M2 5l3 3 3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

const PAGE_SIZE = 50;

const PerformancesTable = ({
  data,
  loading,
  levelMap,
  managersData,
  serversData,
  tableSearch,
  onTableSearchChange,
  onRowClick,
  selectedPeriods,
}) => {
  const { t } = useTranslation();
  const [sortCol, setSortCol] = useState('diamonds');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('desc');
    }
    setPage(1);
  };

  // Build tier evolution map: for each tiktok_id, compare tier_p across periods
  const tierEvolutionMap = useMemo(() => {
    if (!selectedPeriods?.length || selectedPeriods?.length < 2) return {};
    const sortedPeriods = [...selectedPeriods]?.sort();
    const latestPeriod = sortedPeriods?.[sortedPeriods?.length - 1];
    const prevPeriod = sortedPeriods?.[sortedPeriods?.length - 2];
    const map = {};
    const byTiktok = {};
    data?.forEach(r => {
      if (!byTiktok?.[r?.tiktok_id]) byTiktok[r.tiktok_id] = {};
      byTiktok[r.tiktok_id][r.period] = r?.tier_p;
    });
    Object.entries(byTiktok)?.forEach(([tiktokId, periods]) => {
      const curr = periods?.[latestPeriod];
      const prev = periods?.[prevPeriod];
      if (curr !== undefined && prev !== undefined && curr !== null && prev !== null) {
        if (curr < prev) map[tiktokId] = 'up';
        else if (curr > prev) map[tiktokId] = 'down';
        else map[tiktokId] = 'maintained';
      }
    });
    return map;
  }, [data, selectedPeriods]);

  // Client-side search — FIX A2: include display_name in search
  const searchFiltered = useMemo(() => {
    if (!tableSearch?.trim()) return data;
    const term = tableSearch?.trim()?.toLowerCase();
    return data?.filter(r =>
      r?.username?.toLowerCase()?.includes(term) ||
      r?.display_name?.toLowerCase()?.includes(term) ||
      r?.tiktok_id?.toLowerCase()?.includes(term)
    );
  }, [data, tableSearch]);

  const sorted = useMemo(() => {
    if (!searchFiltered?.length) return [];
    return [...searchFiltered]?.sort((a, b) => {
      let av = a?.[sortCol];
      let bv = b?.[sortCol];
      const numCols = ['diamonds', 'total_bonus', 'net_agency', 'live_days', 'live_hours', 'charge', 'activeness_bonus', 'rankup_bonus', 'incremental_bonus', 'tier_p'];
      if (numCols?.includes(sortCol)) {
        av = parseFloat(av) || 0;
        bv = parseFloat(bv) || 0;
      } else {
        av = (av || '')?.toString()?.toLowerCase();
        bv = (bv || '')?.toString()?.toLowerCase();
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [searchFiltered, sortCol, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted?.length / PAGE_SIZE));
  const pageData = sorted?.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleExportCSV = () => {
    const headers = ['Rank', 'Username', 'Display Name', t('admin.performance.server', 'Serveur'), t('admin.performance.period', 'Période'), 'Manager', 'Level', 'Diamonds', 'Live Hours', 'Live Days', 'Tier P', t('admin.performance.activeness', 'Activeness'), t('admin.performance.bonusActiveness', 'Bonus Act.'), t('admin.performance.bonusRankup', 'Bonus Rankup'), t('admin.performance.bonusIncremental', 'Bonus Incr.'), t('admin.performance.totalBonus', 'Total Bonus'), t('admin.performance.charge', 'Charge'), t('admin.performance.netAgency', 'Net Agence'), t('admin.performance.violative', 'Violatif')];
    const rows = sorted?.map((r, i) => {
      const managerName = r?.users?.name || '—';
      const level = levelMap?.[r?.tiktok_id];
      const isViolative = r?.is_violative;
      return [
        i + 1,
        r?.username || '',
        r?.display_name || '',
        r?.server || '',
        r?.period || '',
        managerName,
        level,
        r?.diamonds || 0,
        formatHours(r?.live_hours),
        r?.live_days || 0,
        r?.tier_p !== null && r?.tier_p !== undefined ? `T${r?.tier_p}` : '',
        r?.activeness_level || 0,
        r?.activeness_bonus || 0,
        r?.rankup_bonus || 0,
        r?.incremental_bonus || 0,
        r?.total_bonus || 0,
        r?.charge || 0,
        r?.net_agency || 0,
        isViolative ? t('admin.performance.yes', 'Oui') : t('admin.performance.no', 'Non'),
      ];
    });
    const csv = [headers, ...rows]?.map(row => row?.map(v => `"${v}"`)?.join(','))?.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'performances_export.csv';
    a?.click();
    URL.revokeObjectURL(url);
  };

  const columns = [
    { key: 'rank', label: '#', sortable: false },
    { key: 'username', label: t('admin.performance.username', 'Username'), sortable: true },
    { key: 'display_name', label: t('admin.performance.displayName', 'Display Name'), sortable: true },
    { key: 'server', label: t('admin.performance.server', 'Serveur'), sortable: true },
    { key: 'period', label: t('admin.performance.period', 'Période'), sortable: true },
    { key: 'manager', label: t('admin.performance.manager', 'Manager'), sortable: false },
    { key: 'level', label: t('admin.performance.level', 'Level'), sortable: false },
    { key: 'diamonds', label: 'Diamonds', sortable: true },
    { key: 'live_hours', label: t('admin.performance.liveHours', 'Live Hours'), sortable: true },
    { key: 'live_days', label: t('admin.performance.liveDays', 'Live Days'), sortable: true },
    { key: 'tier_p', label: t('admin.performance.tierP', 'Tier P'), sortable: true },
    { key: 'tier_evo', label: t('admin.performance.evolution', 'Évolution'), sortable: false },
    { key: 'activeness_level', label: t('admin.performance.activeness', 'Activeness'), sortable: true },
    { key: 'activeness_bonus', label: t('admin.performance.bonusActiveness', 'Bonus Act.'), sortable: true },
    { key: 'rankup_bonus', label: t('admin.performance.bonusRankup', 'Bonus Rankup'), sortable: true },
    { key: 'incremental_bonus', label: t('admin.performance.bonusIncremental', 'Bonus Incr.'), sortable: true },
    { key: 'total_bonus', label: t('admin.performance.totalBonus', 'Total Bonus'), sortable: true },
    { key: 'charge', label: t('admin.performance.charge', 'Charge'), sortable: true },
    { key: 'net_agency', label: t('admin.performance.netAgency', 'Net Agence'), sortable: true },
    { key: 'is_violative', label: t('admin.performance.violative', 'Violatif'), sortable: false },
  ];

  const SkeletonRow = () => (
    <tr>
      {columns?.map(c => (
        <td key={c?.key} className="px-4 py-3">
          <div className="h-3 rounded animate-pulse" style={{ backgroundColor: '#1f1f1f', width: '60%' }} />
        </td>
      ))}
    </tr>
  );

  const TierEvoCell = ({ tiktokId }) => {
    const evo = tierEvolutionMap?.[tiktokId];
    if (!evo) return <span style={{ color: 'rgba(255,255,255,0.2)' }}>—</span>;
    if (evo === 'up') return <span style={{ color: '#22c55e' }}>↑ up</span>;
    if (evo === 'down') return <span style={{ color: '#ef4444' }}>↓ down</span>;
    return <span style={{ color: 'rgba(255,255,255,0.4)' }}>→ =</span>;
  };

  return (
    <div>
      {/* Search bar */}
      <div className="flex items-center justify-between mb-3 gap-3">
        <div className="relative flex-1" style={{ maxWidth: '280px' }}>
          <svg
            className="absolute left-2.5 top-1/2 -translate-y-1/2"
            width="13" height="13" viewBox="0 0 13 13" fill="none"
            style={{ color: 'rgba(255,255,255,0.3)' }}
          >
            <circle cx="5.5" cy="5.5" r="4" stroke="currentColor" strokeWidth="1.4" />
            <path d="M9 9l2.5 2.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            placeholder={t('admin.performance.searchCreator', 'Rechercher un créateur...')}
            value={tableSearch || ''}
            onChange={e => onTableSearchChange?.(e?.target?.value)}
            className="w-full text-base rounded-lg pl-8 pr-3 py-1.5 outline-none border"
            style={{
              backgroundColor: '#111111',
              borderColor: tableSearch ? 'rgba(229,32,42,0.3)' : '#1f1f1f',
              color: 'rgba(255,255,255,0.8)',
              fontFamily: 'Inter, sans-serif',
            }}
          />
        </div>
        <p className="text-xs" style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif' }}>
          {loading ? '...' : `${sorted?.length} ${t('admin.performance.creatorsCount', 'créateurs')}`} · {t('admin.performance.page', 'Page')} {page}/{totalPages}
        </p>
        <button
          onClick={handleExportCSV}
          disabled={loading || !sorted?.length}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border"
          style={{
            backgroundColor: '#111111',
            borderColor: '#1f1f1f',
            color: 'rgba(255,255,255,0.6)',
            fontFamily: 'Inter, sans-serif',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = '#22c55e'; e.currentTarget.style.color = '#22c55e'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = '#1f1f1f'; e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}
        >
          <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
            <path d="M6.5 1v8M3.5 6.5l3 3 3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M1 10v1a1 1 0 001 1h9a1 1 0 001-1v-1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          </svg>
          {t('admin.performance.exportCsv', 'Export CSV')}
        </button>
      </div>

      {/* Table */}
      <div className="rounded-xl overflow-hidden border" style={{ borderColor: '#1f1f1f' }}>
        <div className="overflow-x-auto">
          <table className="w-full" style={{ borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#0d0d0d', borderBottom: '1px solid #1f1f1f' }}>
                {columns?.map(col => (
                  <th
                    key={col?.key}
                    className="px-4 py-3 text-left"
                    onClick={() => col?.sortable && handleSort(col?.key)}
                    style={{ cursor: col?.sortable ? 'pointer' : 'default', userSelect: 'none', whiteSpace: 'nowrap' }}
                  >
                    <div className="flex items-center gap-1.5">
                      <span
                        className="text-xs font-semibold uppercase tracking-wider"
                        style={{
                          color: sortCol === col?.key ? '#E5202A' : 'rgba(255,255,255,0.35)',
                          fontFamily: 'Inter, sans-serif',
                        }}
                      >
                        {col?.label}
                      </span>
                      {col?.sortable && <SortIcon column={col?.key} sortCol={sortCol} sortDir={sortDir} />}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 10 })?.map((_, i) => <SkeletonRow key={i} />)
              ) : pageData?.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns?.length}
                    className="px-4 py-12 text-center text-sm"
                    style={{ color: 'rgba(255,255,255,0.25)', fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('admin.performance.noResults', 'Aucun résultat pour ces filtres')}
                  </td>
                </tr>
              ) : (
                pageData?.map((row, idx) => {
                  const globalRank = (page - 1) * PAGE_SIZE + idx + 1;
                  const managerName = row?.users?.name || '—';
                  const level = levelMap?.[row?.tiktok_id];
                  const isViolative = row?.is_violative;
                  return (
                    <tr
                      key={`${row?.tiktok_id}-${row?.period}-${idx}`}
                      onClick={() => row?.tiktok_id && onRowClick?.(row?.tiktok_id)}
                      style={{
                        borderBottom: '1px solid #1a1a1a',
                        cursor: 'pointer',
                        borderLeft: isViolative ? '2px solid #ef4444' : undefined,
                        backgroundColor: row?.is_estimated ? 'rgba(249,115,22,0.04)' : 'transparent',
                      }}
                      onMouseEnter={e => (e.currentTarget.style.backgroundColor = row?.is_estimated ? 'rgba(249,115,22,0.08)' : '#161616')}
                      onMouseLeave={e => (e.currentTarget.style.backgroundColor = row?.is_estimated ? 'rgba(249,115,22,0.04)' : 'transparent')}
                    >
                      <td className="px-4 py-3">
                        <span className="text-xs font-bold" style={{ color: 'rgba(255,255,255,0.25)', fontFamily: 'Sora, sans-serif' }}>
                          {globalRank}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm font-medium" style={{ color: 'rgba(255,255,255,0.85)', fontFamily: 'Inter, sans-serif' }}>
                          {row?.username || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs" style={{ color: 'rgba(255,255,255,0.55)', fontFamily: 'Inter, sans-serif' }}>
                          {row?.display_name || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <ServerBadge server={row?.server} serversData={serversData} />
                      </td>
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-1.5 text-xs font-medium" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Inter, sans-serif' }}>
                          {row?.period || '—'}
                          {row?.is_estimated && (
                            <span className="px-1 py-0.5 rounded text-[8px] font-bold" style={{ backgroundColor: 'rgba(249,115,22,0.12)', color: '#f97316', border: '1px solid rgba(249,115,22,0.25)' }}>
                              {t('admin.performance.estimated', 'Estimé')}
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs" style={{ color: 'rgba(255,255,255,0.55)', fontFamily: 'Inter, sans-serif' }}>
                          {managerName}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <LevelBadge level={level} />
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm font-semibold" style={{ color: '#f59e0b', fontFamily: 'Inter, sans-serif' }}>
                          {formatNum(row?.diamonds)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs" style={{ color: 'rgba(255,255,255,0.6)', fontFamily: 'Inter, sans-serif' }}>
                          {formatHours(row?.live_hours)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs" style={{ color: 'rgba(255,255,255,0.6)', fontFamily: 'Inter, sans-serif' }}>
                          {row?.live_days ?? '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <TierBadge tierP={row?.tier_p} />
                      </td>
                      <td className="px-4 py-3 text-xs">
                        <TierEvoCell tiktokId={row?.tiktok_id} />
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Inter, sans-serif' }}>
                          {row?.activeness_level ?? '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs" style={{ color: '#22c55e', fontFamily: 'Inter, sans-serif' }}>
                          {formatUSD(row?.activeness_bonus)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs" style={{ color: '#22c55e', fontFamily: 'Inter, sans-serif' }}>
                          {formatUSD(row?.rankup_bonus)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs" style={{ color: '#22c55e', fontFamily: 'Inter, sans-serif' }}>
                          {formatUSD(row?.incremental_bonus)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-semibold" style={{ color: '#22c55e', fontFamily: 'Inter, sans-serif' }}>
                          {formatUSD(row?.total_bonus)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs" style={{ color: '#ef4444', fontFamily: 'Inter, sans-serif' }}>
                          {formatUSD(row?.charge)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Inter, sans-serif' }}>
                          {formatUSD(row?.net_agency)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {isViolative ? (
                          <span className="px-2 py-0.5 rounded text-xs font-bold" style={{ backgroundColor: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)' }}>
                            {t('admin.performance.yes', 'Oui')}
                          </span>
                        ) : (
                          <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: '12px' }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1.5 rounded-lg text-xs border transition-all"
            style={{
              backgroundColor: '#111111',
              borderColor: '#1f1f1f',
              color: page === 1 ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.6)',
              fontFamily: 'Inter, sans-serif',
            }}
          >
            ← {t('admin.performance.prev', 'Préc')}
          </button>
          <span className="text-xs" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}>
            {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-3 py-1.5 rounded-lg text-xs border transition-all"
            style={{
              backgroundColor: '#111111',
              borderColor: '#1f1f1f',
              color: page === totalPages ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.6)',
              fontFamily: 'Inter, sans-serif',
            }}
          >
            {t('admin.performance.next', 'Suiv')} →
          </button>
        </div>
      )}
    </div>
  );
};

export default PerformancesTable;
